import { Box, Flex, Heading, Label, Text } from '@workwhile/ui'

import _ from 'lodash'
import { useShiftAssessments } from 'queries/user'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { useShiftDetailContextValue } from '../../context/ShiftDetailProvider'
import { Work } from '../../types'
import { WorkerRow } from '../WorkerRow/WorkerRow'

const FILTER_KEYS = ['scheduled', 'unevaluated', 'pass', 'fail'] as const
type FilterKeys = (typeof FILTER_KEYS)[number]

const FilterLabel = styled(Label)`
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary200};
    color: ${({ theme }) => theme.colors.neutrals[400]};
    background-color: ${({ theme }) => theme.colors.primary50};
  }
`

const DEFAULT_ENALBLED_FILTERS = {
  unevaluated: false,
  scheduled: false,
  pass: false,
  fail: false,
}

export const Workers = () => {
  const {
    state: { work, id: shiftId },
  } = useShiftDetailContextValue()
  const { data: assessments } = useShiftAssessments(shiftId)
  const [enabledFilters, setEnabledFilters] = useState<
    Record<FilterKeys, boolean>
  >(DEFAULT_ENALBLED_FILTERS)

  const filterTypes = useMemo(
    () => ({
      unevaluated: (w: Work) =>
        w.worker?.id &&
        assessments?.assessmentsByWorkerId?.[w.worker?.id]?.status ===
          'needs_evaluation',
      pass: (w: Work) =>
        w.worker?.id &&
        assessments?.assessmentsByWorkerId?.[w.worker?.id]?.status === 'pass',
      fail: (w: Work) =>
        w.worker?.id &&
        assessments?.assessmentsByWorkerId?.[w.worker?.id]?.status === 'fail',
      scheduled: (w: Work) => w.status == 'scheduled',
    }),
    [assessments]
  )

  const workers = useMemo(
    () =>
      work
        .filter((w) =>
          FILTER_KEYS.every(
            (key) => !enabledFilters[key] || filterTypes[key](w)
          )
        )
        .map((w, index) => {
          return (
            <WorkerRow key={`worker-${w.id}-${index}`} work={w} allowEdit />
          )
        }),
    [work, enabledFilters, filterTypes]
  )

  const filterTotals = useMemo(() => {
    return FILTER_KEYS.reduce(
      (acc, key) => {
        const total = work.filter(filterTypes[key]).length
        acc[key] = total

        // Side effect: If the filter has no results, disable the filter
        if (!total) {
          setEnabledFilters((prev) => ({
            ...prev,
            [key]: false,
          }))
        }

        return acc
      },
      {} as Record<FilterKeys, number>
    )
  }, [work, filterTypes, setEnabledFilters])

  const filterButtons = useMemo(
    () =>
      FILTER_KEYS.filter((key) => filterTotals[key] > 0).map((key) => (
        <FilterLabel
          key={key}
          variant={enabledFilters[key] ? 'primary' : 'none'}
          onClick={() =>
            setEnabledFilters((prev) => ({
              ...prev,
              [key]: !prev[key],
            }))
          }
        >
          {_.capitalize(key)} ({filterTotals[key]})
        </FilterLabel>
      )),
    [enabledFilters, filterTotals, setEnabledFilters]
  )

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center">
        <Heading level={2} fontWeight="400">
          Workers
        </Heading>
        <Flex gap={3}>{filterButtons}</Flex>
      </Flex>

      <Flex gap={3} flexDirection="column">
        {workers.length > 0 ? (
          workers
        ) : (
          <Text color="lightText">No workers scheduled</Text>
        )}
      </Flex>
    </Box>
  )
}
